import { useCallback, useMemo } from "react";
import { Chart, ChartDataset } from "chart.js";
import {
  forEach,
  head,
  map,
  orderBy,
  reduce,
  size,
  slice,
  sum,
  take,
} from "lodash";
import { transparentize } from "@glossy";
import { background, chart, content, useTheme } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { endOf, getDatesFromRange, startOf } from "@tootools/date";
import { tz } from "@tootools/date";
import { natural } from "@tootools/sort";
import { tr } from "@radarly/i18n";
import { Serie } from "../models";

type ChartyDataSets = Omit<Serie, "data"> & ChartDataset<"line">;

const getPointRadius = (context) => {
  const xAxis = context.chart.scales["x"];
  const tickWidth = xAxis.width / (xAxis.max - xAxis.min);
  return tickWidth > 40 ? 4 : 0;
};

const buildData = (
  datasets: Serie[],
  datesFromRanges: AbstractDate[],
  theme: GlossyTheme
): ChartyDataSets[] => {
  return map(datasets, (dataset) => {
    const color = dataset.color || chart({ theme }, "primary");
    return {
      id: dataset.id,
      label: dataset.label,
      data: map(dataset.data, "count"),
      spanGaps: true,
      tension: 0.1,
      pointBackgroundColor: background({ theme }),
      pointBorderWidth: 3,
      pointBorderColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: transparentize(0.8, color),
      pointHoverBorderWidth: 24,
      pointRadius: getPointRadius,
      pointHitRadius: 16,
      pointHoverRadius: 6,
      borderColor: color,
      backgroundColor: color,
      accentColor: color,
      accentFadedColor: content({ theme }, "tertiary"),
      clip: 24,
    };
  });
};

function sortHoveredDataset(dataset: ChartyDataSets[], hover: string) {
  if (size(dataset) > 0) {
    const index = !hover ? 0 : dataset.findIndex((e) => e.id === hover);
    const hoveredDataset = [...dataset].filter((_, i) => i !== index);
    hoveredDataset.unshift(dataset[index]);
    return hoveredDataset;
  } else {
    return dataset;
  }
}

export const useOtherSeriesIds = (series: Serie[]) =>
  useMemo(() => {
    const orderedSeries = orderBy(series, "count", "desc");
    const otherSeries = slice(orderedSeries, 20);
    return map(otherSeries, "id");
  }, [series]);

export const useSeries = (
  series: Serie[],
  locale: Locale,
  theme: GlossyTheme
): Serie[] =>
  useMemo(() => {
    if (series.length > 21) {
      const orderedSeries = orderBy(series, "total", "desc");
      const mainSeries = take(orderedSeries, 20);
      const otherSeries = slice(orderedSeries, 20);
      const otherData = reduce(
        otherSeries,
        (acc, serie) => {
          forEach(serie.data, ({ count, date }, key) => {
            acc[key]
              ? (acc[key] = {
                  count: acc[key]?.count + count,
                  date,
                })
              : acc.push({ count, date });
          });
          return acc;
        },
        []
      );
      return [
        ...mainSeries,
        {
          id: "other",
          label: tr(locale, "CHARTY::OTHER_SERIE"),
          data: otherData,
          color: content({ theme }, "secondary"),
          type: series[0].type,
        },
      ];
    } else {
      return series;
    }
  }, [series, locale, theme]);

export function useLegendSeries(
  series: Serie[],
  legendSort?: "alpha" | "volumeDesc"
) {
  const theme = useTheme();
  return useMemo(() => {
    const legends = map(series, (serie) => ({
      id: serie.id,
      label: serie.label,
      count: sum(map(serie.data, "count")),
      color: serie.color || chart({ theme }, "primary"),
      isComparaisonPeriod: serie.isComparaisonPeriod,
    }));

    return legendSort === "alpha"
      ? natural(legends, "label")
      : legendSort === "volumeDesc"
      ? orderBy(legends, "count", "desc")
      : legends;
  }, [series, theme, legendSort]);
}

const includeEndOfRange = (data, range) =>
  size(data) > size(getDatesFromRange(range));

export function useDatesFromRange(range: Range, withEndOfRange: boolean) {
  return useMemo(() => {
    return getDatesFromRange(range, withEndOfRange);
  }, [range, withEndOfRange]);
}

export function useOnClickPoint(
  datasets: any,
  series: Serie[],
  range: Range,
  timezone: Timezone,
  onClickPoint: (uid: Serie["id"], range: Omit<Range, "interval">) => void
) {
  return useCallback(
    (datasetIndex: number, index: number) => {
      const datesFromRanges = getDatesFromRange(range);
      const data = datasets[datasetIndex];
      const date = data.data[index].date || datesFromRanges[index];
      if (onClickPoint) {
        onClickPoint(series[datasetIndex].id, {
          from: startOf(tz(date, timezone), range.interval).toISOString(),
          to: endOf(tz(date, timezone), range.interval).toISOString(),
        });
      }
    },
    [datasets, onClickPoint, range, series, timezone]
  );
}

export function useDatasets(series: Serie[], range: Range, hovered: string) {
  const theme = useTheme();
  const withEndOfRange = includeEndOfRange(head(series)?.data, range);
  const datesFromRanges = useDatesFromRange(range, withEndOfRange);
  const datasets = useMemo(() => {
    return buildData(series, datesFromRanges, theme);
  }, [series, datesFromRanges, theme]);
  return sortHoveredDataset(datasets, hovered);
}

export function useChartData(datasets: ChartyDataSets[], range: Range) {
  const withEndOfRange = includeEndOfRange(head(datasets)?.data, range);
  const datesFromRanges = useDatesFromRange(range, withEndOfRange);
  return useMemo(() => {
    return {
      datasets,
      labels: map(datesFromRanges, (date) => date.toISOString(true)),
    };
  }, [datasets, datesFromRanges]);
}

export function updateLineChartOnHover(
  lineChart: Chart,
  hovered: string,
  theme: GlossyTheme
) {
  for (let i = 0; i < lineChart.data.datasets.length; i++) {
    const dataset = lineChart.data.datasets[i] as ChartyDataSets;
    // @ts-ignore accentColor not recognized
    const accentColor = dataset.accentColor;
    // @ts-ignore accentFadedColor not recognized
    const accentFadedColor = dataset.accentFadedColor;

    if (hovered) {
      if (dataset.id === hovered) {
        dataset.borderColor = accentColor;
      } else {
        dataset.borderColor = accentFadedColor;
        dataset.pointRadius = () => 0;
        dataset.pointBorderWidth = 0;
        dataset.pointBorderColor = accentFadedColor;
        dataset.pointBackgroundColor = accentFadedColor;
      }
    } else {
      dataset.borderColor = accentColor;
      dataset.pointBorderWidth = 3;
      dataset.pointBorderColor = accentColor;
      dataset.pointBackgroundColor = background({ theme });
      dataset.pointRadius = getPointRadius;
    }
  }
}
